import { ROUNDNESS } from "../utils/constants";
import type { ElementOrToolType } from "../utils/types";
import type { MarkNonNullable } from "../utils/utility-types";
import { assertNever } from "../utils/utils";
import type {
  SchemadrawElement,
  SchemadrawTextElement,
  SchemadrawEmbeddableElement,
  SchemadrawLinearElement,
  SchemadrawBindableElement,
  SchemadrawFreeDrawElement,
  InitializedSchemadrawImageElement,
  SchemadrawImageElement,
  SchemadrawTextElementWithContainer,
  SchemadrawTextContainer,
  SchemadrawFrameElement,
  RoundnessType,
  SchemadrawFrameLikeElement,
  SchemadrawElementType,
  SchemadrawIframeElement,
  SchemadrawIframeLikeElement,
  SchemadrawMagicFrameElement,
  SchemadrawArrowElement,
} from "./types";

export const isInitializedImageElement = (
  element: SchemadrawElement | null,
): element is InitializedSchemadrawImageElement => {
  return !!element && element.type === "image" && !!element.fileId;
};

export const isImageElement = (
  element: SchemadrawElement | null,
): element is SchemadrawImageElement => {
  return !!element && element.type === "image";
};

export const isEmbeddableElement = (
  element: SchemadrawElement | null | undefined,
): element is SchemadrawEmbeddableElement => {
  return !!element && element.type === "embeddable";
};

export const isIframeElement = (
  element: SchemadrawElement | null,
): element is SchemadrawIframeElement => {
  return !!element && element.type === "iframe";
};

export const isIframeLikeElement = (
  element: SchemadrawElement | null,
): element is SchemadrawIframeLikeElement => {
  return (
    !!element && (element.type === "iframe" || element.type === "embeddable")
  );
};

export const isTextElement = (
  element: SchemadrawElement | null,
): element is SchemadrawTextElement => {
  return element != null && element.type === "text";
};

export const isFrameElement = (
  element: SchemadrawElement | null,
): element is SchemadrawFrameElement => {
  return element != null && element.type === "frame";
};

export const isMagicFrameElement = (
  element: SchemadrawElement | null,
): element is SchemadrawMagicFrameElement => {
  return element != null && element.type === "magicframe";
};

export const isFrameLikeElement = (
  element: SchemadrawElement | null,
): element is SchemadrawFrameLikeElement => {
  return (
    element != null &&
    (element.type === "frame" || element.type === "magicframe")
  );
};

export const isFreeDrawElement = (
  element?: SchemadrawElement | null,
): element is SchemadrawFreeDrawElement => {
  return element != null && isFreeDrawElementType(element.type);
};

export const isFreeDrawElementType = (
  elementType: SchemadrawElementType,
): boolean => {
  return elementType === "freedraw";
};

export const isLinearElement = (
  element?: SchemadrawElement | null,
): element is SchemadrawLinearElement => {
  return element != null && isLinearElementType(element.type);
};

export const isArrowElement = (
  element?: SchemadrawElement | null,
): element is SchemadrawArrowElement => {
  return element != null && element.type === "arrow";
};

export const isLinearElementType = (
  elementType: ElementOrToolType,
): boolean => {
  return (
    elementType === "arrow" || elementType === "line" // || elementType === "freedraw"
  );
};

export const isBindingElement = (
  element?: SchemadrawElement | null,
  includeLocked = true,
): element is SchemadrawLinearElement => {
  return (
    element != null &&
    (!element.locked || includeLocked === true) &&
    isBindingElementType(element.type)
  );
};

export const isBindingElementType = (
  elementType: ElementOrToolType,
): boolean => {
  return elementType === "arrow";
};

export const isBindableElement = (
  element: SchemadrawElement | null | undefined,
  includeLocked = true,
): element is SchemadrawBindableElement => {
  return (
    element != null &&
    (!element.locked || includeLocked === true) &&
    (element.type === "rectangle" ||
      element.type === "diamond" ||
      element.type === "ellipse" ||
      element.type === "image" ||
      element.type === "iframe" ||
      element.type === "embeddable" ||
      element.type === "frame" ||
      element.type === "magicframe" ||
      (element.type === "text" && !element.containerId))
  );
};

export const isTextBindableContainer = (
  element: SchemadrawElement | null,
  includeLocked = true,
): element is SchemadrawTextContainer => {
  return (
    element != null &&
    (!element.locked || includeLocked === true) &&
    (element.type === "rectangle" ||
      element.type === "diamond" ||
      element.type === "ellipse" ||
      isArrowElement(element))
  );
};

export const isSchemadrawElement = (
  element: any,
): element is SchemadrawElement => {
  const type: SchemadrawElementType | undefined = element?.type;
  if (!type) {
    return false;
  }
  switch (type) {
    case "text":
    case "diamond":
    case "rectangle":
    case "iframe":
    case "embeddable":
    case "ellipse":
    case "arrow":
    case "freedraw":
    case "line":
    case "frame":
    case "magicframe":
    case "image":
    case "selection": {
      return true;
    }
    default: {
      assertNever(type, null);
      return false;
    }
  }
};

export const hasBoundTextElement = (
  element: SchemadrawElement | null,
): element is MarkNonNullable<SchemadrawBindableElement, "boundElements"> => {
  return (
    isTextBindableContainer(element) &&
    !!element.boundElements?.some(({ type }) => type === "text")
  );
};

export const isBoundToContainer = (
  element: SchemadrawElement | null,
): element is SchemadrawTextElementWithContainer => {
  return (
    element !== null &&
    "containerId" in element &&
    element.containerId !== null &&
    isTextElement(element)
  );
};

export const isUsingAdaptiveRadius = (type: string) =>
  type === "rectangle" ||
  type === "embeddable" ||
  type === "iframe" ||
  type === "image";

export const isUsingProportionalRadius = (type: string) =>
  type === "line" || type === "arrow" || type === "diamond";

export const canApplyRoundnessTypeToElement = (
  roundnessType: RoundnessType,
  element: SchemadrawElement,
) => {
  if (
    (roundnessType === ROUNDNESS.ADAPTIVE_RADIUS ||
      // if legacy roundness, it can be applied to elements that currently
      // use adaptive radius
      roundnessType === ROUNDNESS.LEGACY) &&
    isUsingAdaptiveRadius(element.type)
  ) {
    return true;
  }
  if (
    roundnessType === ROUNDNESS.PROPORTIONAL_RADIUS &&
    isUsingProportionalRadius(element.type)
  ) {
    return true;
  }

  return false;
};

export const getDefaultRoundnessTypeForElement = (
  element: SchemadrawElement,
) => {
  if (isUsingProportionalRadius(element.type)) {
    return {
      type: ROUNDNESS.PROPORTIONAL_RADIUS,
    };
  }

  if (isUsingAdaptiveRadius(element.type)) {
    return {
      type: ROUNDNESS.ADAPTIVE_RADIUS,
    };
  }

  return null;
};
